import {
  certificatesAttachmentsCreate,
  certificatesAttachmentsList,
  certificatesAttachmentsPartialUpdate,
} from '~/data/generated/endpoints/certificates/certificates';
import {
  inspectionReportsAttachmentsCreate,
  inspectionReportsAttachmentsList,
  inspectionReportsAttachmentsPartialUpdate,
} from '~/data/generated/endpoints/inspection-reports/inspection-reports';
import {
  invoicesAttachmentsCreate,
  invoicesAttachmentsList,
  invoicesAttachmentsPartialUpdate,
} from '~/data/generated/endpoints/invoices/invoices';
import {
  ticketsAttachmentsCreate,
  ticketsAttachmentsList,
} from '~/data/generated/endpoints/tickets/tickets';
import type {
  CertificateAttachment,
  InspectionReportAttachment,
  InvoiceAttachment,
  TicketAttachment,
} from '~/data/generated/model';
import { CertificatesAttachmentsListResponse } from '~/data/generated/zod/certificates/certificates.zod';
import { InspectionReportsAttachmentsListResponse } from '~/data/generated/zod/inspection-reports/inspection-reports.zod';
import { InvoicesAttachmentsListResponse } from '~/data/generated/zod/invoices/invoices.zod';
import { parseApiResponse } from '~/data/parseApiResponse';
import { ticketAttachmentsListSchema } from '~/data/schemas/attachment';

export const associateFileToEntity = async (
  attachmentId: string,
  entityId: string,
  entityType: 'invoice' | 'ticket' | 'certificate' | 'violation' | 'inspection_report' = 'invoice',
  {
    isPrimary = false,
    isNextPage = false,
    pageNumber = 1,
    isVoid = false,
  }: {
    isPrimary?: boolean;
    isNextPage?: boolean;
    pageNumber?: number;
    isVoid?: boolean;
  },
) => {
  // The FK to the parent entity is set server-side from the URL pk, but the
  // generated models require it, so we pass the same id. `is_void` only exists
  // on the invoice attachment serializer; the other entities omit it.
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
  switch (entityType) {
    case 'invoice':
      return invoicesAttachmentsCreate(entityId, {
        invoice: entityId,
        attachment: attachmentId,
        isPrimary,
        isNextPage,
        isVoid,
        pageNumber,
      });
    case 'ticket':
      return ticketsAttachmentsCreate(entityId, {
        ticket: entityId,
        attachment: attachmentId,
        isPrimary,
        isNextPage,
        pageNumber,
      });
    case 'certificate':
      return certificatesAttachmentsCreate(entityId, {
        certificate: entityId,
        attachment: attachmentId,
        isPrimary,
        isNextPage,
        pageNumber,
      });
    case 'inspection_report':
      return inspectionReportsAttachmentsCreate(entityId, {
        inspectionReport: entityId,
        attachment: attachmentId,
        isPrimary,
        isNextPage,
        pageNumber,
      });
    default:
      console.error(`Unsupported entity type: ${entityType}`);
  }
};

export const updateAssociationFileToEntity = async (
  attachmentId: string,
  entityId: string,
  entityType: 'invoice' | 'certificate' | 'inspection_report' = 'invoice',
  {
    isPrimary = false,
    isNextPage = false,
    isVoid = false,
    id,
  }: {
    isPrimary?: boolean;
    isNextPage?: boolean;
    isVoid?: boolean;
    id: string;
  },
) => {
  switch (entityType) {
    case 'invoice':
      return invoicesAttachmentsPartialUpdate(entityId, id, {
        invoice: entityId,
        attachment: attachmentId,
        isPrimary,
        isNextPage,
        isVoid,
      });
    case 'certificate':
      return certificatesAttachmentsPartialUpdate(entityId, id, {
        certificate: entityId,
        attachment: attachmentId,
        isPrimary,
        isNextPage,
      });
    case 'inspection_report':
      return inspectionReportsAttachmentsPartialUpdate(entityId, id, {
        inspectionReport: entityId,
        attachment: attachmentId,
        isPrimary,
        isNextPage,
      });
    default:
      throw new Error(`Unsupported entity type: ${entityType}`);
  }
};

export function getFilesAssociatedToEntity(
  entityId: string,
  entityType: 'invoice',
): Promise<InvoiceAttachment[]>;
export function getFilesAssociatedToEntity(
  entityId: string,
  entityType: 'ticket',
): Promise<TicketAttachment[]>;

export function getFilesAssociatedToEntity(
  entityId: string,
  entityType: 'certificate',
): Promise<CertificateAttachment[]>;

export function getFilesAssociatedToEntity(
  entityId: string,
  entityType: 'inspection_report',
): Promise<InspectionReportAttachment[]>;

export async function getFilesAssociatedToEntity(
  entityId: string,
  entityType: 'invoice' | 'ticket' | 'certificate' | 'inspection_report',
) {
  // These endpoints return a bare array: they use LimitOffset pagination with no
  // default page size, so the paginator never engages without an explicit
  // `limit`, which none of these calls send. The generated types say so as of
  // AMP-619, so every read below parses the one shape that can actually arrive.
  switch (entityType) {
    case 'invoice': {
      const { data } = await invoicesAttachmentsList(entityId);
      const results = parseApiResponse(InvoicesAttachmentsListResponse, data, 'InvoiceAttachment');
      return results.filter((attachment) => attachment.invoice === entityId);
    }
    case 'ticket': {
      const { data } = await ticketsAttachmentsList(entityId);
      return parseApiResponse(ticketAttachmentsListSchema, data, 'TicketAttachment');
    }
    case 'certificate': {
      const { data } = await certificatesAttachmentsList(entityId);
      return parseApiResponse(CertificatesAttachmentsListResponse, data, 'CertificateAttachment');
    }
    case 'inspection_report': {
      const { data } = await inspectionReportsAttachmentsList(entityId);
      return parseApiResponse(
        InspectionReportsAttachmentsListResponse,
        data,
        'InspectionReportAttachment',
      );
    }
    default:
      console.error(`Unsupported entity type: ${entityType}`);
  }
}
